/**
 * compiler-preload.js
 * -----------------------------------------------------------------------
 * "Choose a Device" kartlarındaki "İndir" kısayol butonu için: fiziksel
 * cihaz bağlamadan, Upload akışına hiç girmeden, doğrudan o board'un WASM
 * derleyici toolchain'ini indirip hazırlar. openblock-vm'in dışa açtığı
 * CompilerBridge'in TEK bir paylaşımlı örneğini kullanır — böylece burada
 * indirilen dosyalar, sonradan gerçek bir Upload yapıldığında
 * web-serialport.js'in kullandığı CompilerBridge ile AYNI tarayıcı
 * kaynaklarını (aynı window.__factories, aynı HTTP cache) paylaşır.
 *
 * ÖNEMLİ: AVR derleyicisi Uno/Nano/Mega/Leonardo arasında PAYLAŞIMLI —
 * hepsi aynı toolchain dosyalarını kullanıyor. Bu yüzden indirme durumu
 * (downloading/progress) kart bazında DEĞİL, "kind" ('avr'|'esp32')
 * bazında tek bir paylaşımlı state olarak tutuluyor ve subscribe() ile
 * o kind'e ait TÜM kartlara aynı anda yayınlanıyor — Nano'nun indirmesini
 * başlatırsanız Uno/Mega/Leonardo kartları da aynı ilerlemeyi gösterir.
 */

import VM from 'openblock-vm';
import {getCompilerKindForDevice, isCompilerReady} from './compiler-status.js';

let sharedBridge = null;
const getBridge = () => {
    if (!sharedBridge) {
        sharedBridge = new VM.CompilerBridge({
            avrDistDir: '/static/dist-web',
            esp32DistDir: '/static/dist-esp32-web'
        });
    }
    return sharedBridge;
};

// kind -> {downloading: bool, pct: number, error: string|null}
const downloadState = {
    avr: {downloading: false, pct: 0, error: null},
    esp32: {downloading: false, pct: 0, error: null}
};
// kind -> Set<function(state)>
const listeners = {avr: new Set(), esp32: new Set()};
// kind -> in-flight Promise (aynı anda iki ayrı indirme başlatılmasın diye)
const inFlight = {avr: null, esp32: null};

const notify = kind => {
    listeners[kind].forEach(fn => fn(downloadState[kind]));
};

/**
 * Bir kart bileşeni, kendi kind'ine ait paylaşımlı indirme durumundaki
 * değişiklikleri dinlemek için bunu componentDidMount'ta çağırır.
 * @param {'avr'|'esp32'} kind
 * @param {function({downloading:boolean, pct:number, error:?string}):void} fn
 * @returns {function():void} unsubscribe
 */
export const subscribeToDownloadState = (kind, fn) => {
    if (!listeners[kind]) return () => {};
    listeners[kind].add(fn);
    fn(downloadState[kind]); // mevcut durumu hemen ilet
    return () => listeners[kind].delete(fn);
};

/**
 * @param {string} deviceId - ör. 'arduinoUno', 'arduinoEsp32'
 * @returns {Promise<void>} — zaten indiriliyorsa AYNI (paylaşımlı) indirmeyi bekler, yenisini başlatmaz.
 */
export const downloadCompilerForDevice = deviceId => {
    const kind = getCompilerKindForDevice(deviceId);
    if (!kind) {
        return Promise.reject(new Error(`'${deviceId}' için WASM derleyici entegrasyonu yok.`));
    }
    if (isCompilerReady(kind)) {
        return Promise.resolve(); // zaten hazır
    }
    if (inFlight[kind]) {
        return inFlight[kind]; // başka bir kart zaten indiriyor — ona katıl
    }

    const bridge = getBridge();
    downloadState[kind] = {downloading: true, pct: 0, error: null, doneBytes: 0, totalBytes: 0};
    notify(kind);

    // NOT: bilerek async/await KULLANMIYOR — openblock-gui'nin kendi
    // babel/webpack kurulumu regeneratorRuntime polyfill'i global olarak
    // yüklemiyor (kod tabanı hep .then() zinciri kullanıyor); async/await
    // burada "regeneratorRuntime is not defined" hatasına yol açıyordu.
    //
    // preload() SADECE dosyaları indirir, DERLEME YAPMAZ — bu yüzden
    // ilerleme %100'e ulaştığı an gerçekten "hazır" demektir (önceki
    // sürüm compile() kullanıyordu, dosyalar inmiş olsa bile küçük bir
    // deneme derlemesi bitene kadar bar %100'de asılı kalıyordu).
    const p = bridge.preload(kind, (done, total) => {
        // `done`/`total` compiler-bridge.js'ten GERÇEK bayt sayıları olarak
        // geliyor (gzip dosya boyutları toplamı) — sadece yüzde değil, MB
        // cinsinden de gösteriyoruz ki indirmenin gerçekten veri taşıdığı
        // somut olsun (localhost'ta çok hızlı bitip "sahte" görünebiliyor).
        downloadState[kind] = {
            downloading: true,
            pct: Math.round((done / total) * 100),
            error: null,
            doneBytes: done,
            totalBytes: total
        };
        notify(kind);
    })
        .then(() => {
            downloadState[kind] = {downloading: false, pct: 100, error: null};
            notify(kind);
            inFlight[kind] = null;
        })
        .catch(err => {
            downloadState[kind] = {downloading: false, pct: 0, error: err.message};
            notify(kind);
            inFlight[kind] = null;
            throw err;
        });

    inFlight[kind] = p;
    return p;
};
